/**
 * Figure 관련 유틸리티 함수
 */

export interface FigureMatrixResult {
  success: boolean;
  rows: number;
  cols: number;
  items: { marker: string; text: string }[];
  rowLabels: string[];
  colLabels: string[];
}

/**
 * Figure content와 media_note에서 매트릭스 정보 파싱
 */
export const parseFigureMatrix = (content: string, mediaNote: string): FigureMatrixResult => {
  // 1. content에서 NxM 패턴 찾기 (예: "2x2 표", "3x3", "2×3")
  const sizeMatch = content.match(/(\d+)\s*[x×X]\s*(\d+)/);
  if (!sizeMatch) {
    return { success: false, rows: 0, cols: 0, items: [], rowLabels: [], colLabels: [] };
  }
  
  const rows = parseInt(sizeMatch[1], 10);
  const cols = parseInt(sizeMatch[2], 10);
  const expectedItems = rows * cols;
  
  // 2. 다양한 번호/기호 패턴 정의
  const markerPatterns = [
    { regex: /[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]/g, type: 'circled' },
    { regex: /[ⓐⓑⓒⓓⓔⓕⓖⓗⓘⓙⓚⓛⓜⓝⓞⓟⓠⓡⓢⓣⓤⓥⓦⓧⓨⓩ]/g, type: 'circled-alpha' },
    { regex: /\((\d+)\)/g, type: 'paren-num' },
    { regex: /([가나다라마바사아자차카타파하])\./g, type: 'korean' },
    { regex: /([A-Z])\./g, type: 'alpha-upper' },
    { regex: /([a-z])\./g, type: 'alpha-lower' },
  ];
  
  // 3. media_note에서 마커 찾기
  let foundMarkers: string[] = [];
  
  for (const pattern of markerPatterns) {
    const matches = mediaNote.match(pattern.regex);
    if (matches && matches.length >= expectedItems) {
      foundMarkers = matches.slice(0, expectedItems);
      break;
    }
  }
  
  if (foundMarkers.length < expectedItems) {
    return { success: false, rows: 0, cols: 0, items: [], rowLabels: [], colLabels: [] };
  }
  
  // 4. 각 마커 뒤의 텍스트 추출
  const items: { marker: string; text: string }[] = [];
  
  for (let i = 0; i < foundMarkers.length; i++) {
    const currentMarker = foundMarkers[i];
    const nextMarker = foundMarkers[i + 1];
    
    const currentIndex = mediaNote.indexOf(currentMarker);
    let endIndex = nextMarker ? mediaNote.indexOf(nextMarker, currentIndex + 1) : mediaNote.length;
    
    // 쉼표나 마침표로 끝나는 경우 처리
    let text = mediaNote.substring(currentIndex + currentMarker.length, endIndex).trim();
    text = text.replace(/^[\s,.:;]+/, '').replace(/[\s,.:;]+$/, '');
    
    items.push({ marker: currentMarker, text });
  }
  
  // 5. content에서 행/열 라벨 추출 시도
  let rowLabels: string[] = [];
  let colLabels: string[] = [];
  
  // "A/B와(과) C/D" 패턴
  const labelMatch = content.match(/(.+?)\/(.+?)[와과]\s*(.+?)\/(.+?)의/);
  if (labelMatch) {
    rowLabels = [labelMatch[1].trim(), labelMatch[2].trim()];
    colLabels = [labelMatch[3].trim(), labelMatch[4].trim()];
  }
  
  return { success: true, rows, cols, items, rowLabels, colLabels };
};

